import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Receipt, MagnifyingGlass, FilePdf, DownloadSimple, PaperPlaneTilt,
  ClockCounterClockwise, PencilSimple, Trash, Printer, Warning, FolderOpen, Eye, FloppyDisk, Package, BellRinging, ChatCircleText,
  LinkSimple, CheckCircle, DotsThreeVertical, Plus, CopySimple, Wallet, Clock, Prohibit,
} from "@phosphor-icons/react";
import { printPO, previewPO } from "@/lib/poPrint";
import { saveBlobToFolder, autoFilePDF } from "@/lib/localSave";
import { HeroBanner, KPICard } from "@/components/shared";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const OVERDUE_DAYS = 7;
const pendingDays = (p) => {
  if (!["sent", "accepted"].includes(p.status)) return null;
  const since = p.sent_at || p.status_updated_at || p.created_at;
  if (!since) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 86400000));
};
/* cumulative received qty per PO line, from all GRN entries */
const recdMap = (p) => {
  const m = {};
  (p.grns || []).forEach((g) => (g.entries || []).forEach((e) => {
    m[e.item_id] = (m[e.item_id] || 0) + (Number(e.qty_received) || 0);
  }));
  return m;
};
const receiptState = (p) => {
  const items = (p.items || []).filter((it) => Number(it.qty) > 0);
  const m = recdMap(p);
  const ordered = items.reduce((s, it) => s + Number(it.qty || 0), 0);
  const recd = items.reduce((s, it) => s + Math.min(m[it.id] || 0, Number(it.qty || 0)), 0);
  return { ordered, recd, full: ordered > 0 && recd >= ordered - 1e-9, partial: recd > 0 && recd < ordered };
};
const PO_STATUS = [
  { v: "draft", l: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-300", led: "text-zinc-400", icon: FolderOpen, accent: "zinc" },
  { v: "sent", l: "Placed", cls: "bg-blue-100 text-blue-800 border-blue-300", led: "text-blue-400", icon: PaperPlaneTilt, accent: "blue" },
  { v: "accepted", l: "Accepted", cls: "bg-amber-100 text-amber-800 border-amber-300", led: "text-amber-400", icon: Clock, accent: "amber" },
  { v: "material_received", l: "Complete", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", led: "text-emerald-400", icon: CheckCircle, accent: "emerald" },
  { v: "cancelled", l: "Cancelled", cls: "bg-rose-100 text-rose-800 border-rose-300", led: "text-red-400", icon: Prohibit, accent: "rose" },
];

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // all | job | stock
  const [histPO, setHistPO] = useState(null);
  const [archOpen, setArchOpen] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archQ, setArchQ] = useState("");
  const [archFrom, setArchFrom] = useState("");
  const [archTo, setArchTo] = useState("");
  const [grnPO, setGrnPO] = useState(null);
  const [grnQty, setGrnQty] = useState({});
  const [grnNote, setGrnNote] = useState("");
  const [grnDate, setGrnDate] = useState("");
  const [company, setCompany] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [sendChannels, setSendChannels] = useState({ email: true, whatsapp: false });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/company-settings").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  const remindPO = async (p, channel) => {
    const target = channel === "whatsapp" ? p.contact_phone : p.contact_email;
    if (!window.confirm(`Send a ${channel === "whatsapp" ? "WhatsApp" : "email"} follow-up reminder for ${p.po_no} to ${target}?`)) return;
    try {
      const { data } = await api.post(`/purchase-orders/${p.id}/remind`, { channel });
      if (data.note) toast.warning(data.note);
      else toast.success(`Reminder sent via ${data.channel} to ${data.to}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const copyAcceptLink = async (p) => {
    try {
      const { data } = await api.get(`/purchase-orders/${p.id}/accept-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success("Vendor confirmation link copied — paste it into WhatsApp, SMS or anywhere else");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not get the confirmation link"); }
  };

  const openGRN = (p) => {
    setGrnPO(p);
    setGrnQty({});
    setGrnNote("");
    setGrnDate(new Date().toISOString().slice(0, 10));
  };
  const saveGRN = async () => {
    const entries = Object.entries(grnQty)
      .map(([item_id, q]) => ({ item_id, qty_received: Number(q) || 0 }))
      .filter((e) => e.qty_received > 0);
    if (!entries.length) return toast.error("Enter at least one received quantity");
    try {
      await api.post(`/purchase-orders/${grnPO.id}/grn`, { entries, note: grnNote, grn_date: grnDate });
      toast.success("Receipt recorded");
      setGrnPO(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openArchive = async () => {
    try {
      const { data } = await api.get("/po-archive");
      setArchives(data);
      setArchOpen(true);
    } catch { toast.error("Could not load PO archive"); }
  };

  const load = () =>
    api.get("/purchase-orders").then((r) => { setPos(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  /* ---------------- actions (same endpoints as vendor workspace) ---------------- */
  const openSend = (p) => {
    setSendFor(p);
    setSendChannels({ email: !!p.contact_email, whatsapp: !!p.contact_phone });
  };
  const confirmSend = async () => {
    const channels = Object.entries(sendChannels).filter(([, v]) => v).map(([k]) => k);
    if (!channels.length) return toast.error("Pick at least one channel");
    setSending(true);
    try {
      const { data } = await api.post(`/purchase-orders/${sendFor.id}/email`, { channels });
      const revs = (sendFor.revisions || []).length;
      if (data.channels_sent.length) toast.success(`${revs ? "Revised PO" : "PO"} sent via ${data.channels_sent.join(" & ")} — status set to Sent`);
      else toast.warning(`PO marked sent, but delivery is pending — ${data.notes.join("; ")}`);
      setSendFor(null); load();
      try {
        const pdf = await api.get(`/purchase-orders/${sendFor.id}/pdf`, { responseType: "blob" });
        const folder = await autoFilePDF(pdf.data, `${sendFor.po_no}${revs ? `-R${revs}` : ""}.pdf`);
        if (folder) toast.success(`Copy auto-filed to "${folder}"`);
      } catch { /* auto-filing is best-effort */ }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const setPOStatus = async (p, status) => {
    try { await api.post(`/purchase-orders/${p.id}/status`, { status }); toast.success("PO status updated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const downloadFile = async (p, kind) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.po_no}.${kind === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Download failed"); }
  };
  const saveToFolder = async (p) => {
    try {
      const r = await api.get(`/purchase-orders/${p.id}/pdf`, { responseType: "blob" });
      const folder = await saveBlobToFolder(r.data, `${p.po_no}.pdf`);
      if (folder === false) {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${p.po_no}.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.info("The browser blocked the folder picker here (embedded view / unsupported browser) — PDF downloaded instead. Open the portal in its own Chrome/Edge tab to save into a folder of your choice.");
      } else if (folder) {
        toast.success(`${p.po_no}.pdf saved to "${folder}"`);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save to folder"); }
  };
  const delPO = async (p) => {
    if (!window.confirm(`Delete ${p.po_no}? This cannot be undone.`)) return;
    await api.delete(`/purchase-orders/${p.id}`); toast.success("PO deleted"); load();
  };

  /* ---------------- derived ---------------- */
  /* multi-term search: every space/comma separated token must match somewhere */
  const tokens = search.toLowerCase().split(/[\s,]+/).filter(Boolean);
  const filtered = pos.filter((p) => {
    if (statusFilter !== "all" && (p.status || "draft") !== statusFilter) return false;
    if (typeFilter === "job" && !p.order_id) return false;
    if (typeFilter === "stock" && p.order_id) return false;
    const hay = [p.po_no, p.vendor_name, p.ref, p.status, p.created_by,
      ...(p.items || []).map((it) => it.item)].join(" ").toLowerCase();
    return tokens.every((t) => hay.includes(t));
  }).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // FIFO: oldest created first
  // Queue position among POs still pending action (received/cancelled POs are done, no position needed).
  const fifoQueue = filtered.filter((p) => !["material_received", "cancelled"].includes(p.status || "draft")).map((p) => p.id);
  const fifoPosition = (p) => (["material_received", "cancelled"].includes(p.status || "draft") ? null : fifoQueue.indexOf(p.id) + 1);
  const totalValue = pos.reduce((s, p) => s + (p.grand_total || 0), 0);
  const countBy = (st) => pos.filter((p) => (p.status || "draft") === st).length;

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] animate-fade-in">
      <HeroBanner
        testId="pos-hero-banner"
        eyebrow="Procurement — Live"
        title="Purchase Orders"
        subtitle="Every PO to every vendor — status, value and follow-ups in one place."
        right={
          <>
            <Button onClick={() => navigate("/vendors?tab=pos&new=1")} data-testid="pos-new-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5">
              <Plus weight="bold" size={16} /> New Purchase Order
            </Button>
            <Button variant="outline" onClick={openArchive} data-testid="pos-archive-btn"
              className="border-emerald-500/40 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20 rounded-sm gap-1.5">
              <FolderOpen size={16} /> Archive
            </Button>
          </>
        }
      />

      {/* status register strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <KPICard testId="pos-stat-total" label="Total POs" value={pos.length} icon={Receipt} accent="zinc" />
        <KPICard testId="pos-stat-value" label="Total Value" value={`₹ ${inr(totalValue)}`} icon={Wallet} accent="emerald" />
        {PO_STATUS.map((s) => (
          <KPICard key={s.v} testId={`pos-stat-${s.v}`} label={s.l} value={countBy(s.v)} icon={s.icon} accent={s.accent}
            onClick={() => setStatusFilter(statusFilter === s.v ? "all" : s.v)} />
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative w-full max-w-xs">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO no, vendor, ref, item…"
            className="bg-white border-slate-300 rounded-sm text-slate-800 pl-9 focus:border-emerald-600 focus-visible:ring-emerald-600" data-testid="pos-search" />
        </div>
        <div className="flex items-center gap-1.5 border border-slate-300 bg-white rounded-sm p-0.5" data-testid="pos-type-filter">
          {[["all", "All"], ["job", "Job Orders"], ["stock", "Stock Orders"]].map(([v, l]) => (
            <button key={v} onClick={() => setTypeFilter(v)} data-testid={`pos-type-filter-${v}`}
              className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors ${typeFilter === v ? "bg-emerald-600 text-white font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
              {l}
            </button>
          ))}
        </div>
        {statusFilter !== "all" && (
          <button onClick={() => setStatusFilter("all")} data-testid="pos-clear-filter"
            className="text-xs font-mono uppercase tracking-wider text-emerald-700 hover:text-emerald-600 border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 rounded-sm">
            Filter: {PO_STATUS.find((s) => s.v === statusFilter)?.l} × clear
          </button>
        )}
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-bold">PO No</th>
              <th className="px-4 py-3 font-bold">Ref Code</th>
              <th className="px-4 py-3 font-bold">Date of PO with Time</th>
              <th className="px-4 py-3 font-bold">Concerned Person</th>
              <th className="px-4 py-3 font-bold">Customer</th>
              <th className="px-4 py-3 font-bold">Vendor</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold text-right">Value</th>
              <th className="px-4 py-3 font-bold text-center">Action</th>
              <th className="px-4 py-3 font-bold text-right">Edit / Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((p) => {
              const days = pendingDays(p);
              const overdue = days != null && days > OVERDUE_DAYS;
              const receipt = receiptState(p);
              return (
              <tr key={p.id} className={`transition-colors ${overdue ? "bg-rose-50 hover:bg-rose-100" : "hover:bg-slate-50"}`} data-testid={`pos-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-sm text-emerald-700 font-bold">
                  <button type="button" onClick={() => previewPO(p, company)} className="hover:underline" title="Open full-screen preview" data-testid={`pos-preview-${p.id}`}>{p.po_no}</button>
                  <span className={`ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${(p.revisions || []).length ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}
                    title={(p.revisions || []).length ? undefined : "Original issue (no revisions)"} data-testid={`pos-rseries-${p.id}`}>R{(p.revisions || []).length}</span>
                  {fifoPosition(p) === 1 && (
                    <span className="ml-1.5 inline-flex items-center text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-300 bg-amber-100 text-amber-800" title="Oldest pending PO — action this first (FIFO)" data-testid={`pos-fifo-badge-${p.id}`}>FIFO #1</span>
                  )}
                  {fifoPosition(p) > 1 && (
                    <span className="ml-1.5 text-[9px] font-mono text-slate-400" title="Position in the oldest-first work queue" data-testid={`pos-fifo-pos-${p.id}`}>#{fifoPosition(p)}</span>
                  )}
                  {(p.revisions || []).length > 0 && (() => {
                    const last = p.revisions[p.revisions.length - 1];
                    return (
                      <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-700"
                        title={`Rev ${last.rev_no} by ${last.edited_by_initials || last.edited_by} · ${(last.edited_at || "").slice(0, 16).replace("T", " ")}`}
                        data-testid={`pos-rev-badge-${p.id}`}>{last.edited_by_initials || ""} {(last.edited_at || "").slice(5, 16).replace("T", " ")}</span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-testid={`pos-refcode-${p.id}`}>{p.dvepl_code || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-testid={`pos-datetime-${p.id}`}>{(p.created_at || "").slice(0, 16).replace("T", " ") || p.po_date}</td>
                <td className="px-4 py-3 text-slate-700 text-xs" data-testid={`pos-concerned-${p.id}`}>{p.concerned_person || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{p.customer_name || (p.order_id ? <span className="text-slate-300">—</span> : <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-100 border border-sky-300 px-1.5 py-0.5 rounded-sm" data-testid={`pos-stock-badge-${p.id}`}>Stock Order</span>)}</td>
                <td className="px-4 py-3 text-slate-900">{p.vendor_name}
                  <div className="flex items-center gap-2">
                    {p.ref && <span className="text-[10px] text-slate-400 font-mono">Ref. {p.ref}</span>}
                    {days != null && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${overdue ? "bg-rose-100 text-rose-700 border-rose-300" : "text-slate-500 border-slate-300"}`} title={p.nudge_count ? `Vendor auto-nudged ${p.nudge_count}× by email` : "Auto email nudge goes out after 7 days pending"} data-testid={`pos-pending-${p.id}`}>
                        {overdue && <Warning size={11} weight="fill" />}{days}d pending{p.nudge_count ? ` · nudged ${p.nudge_count}×` : ""}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Select value={p.status || "draft"} onValueChange={(v) => setPOStatus(p, v)}>
                    <SelectTrigger className={`h-7 w-40 text-[11px] border px-2.5 rounded-full font-semibold ${PO_STATUS.find((s) => s.v === (p.status || "draft"))?.cls}`} data-testid={`pos-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-800">
                      {PO_STATUS.map((s) => <SelectItem key={s.v} value={s.v} className="text-xs">{s.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {p.accepted_at && (
                    <div className="mt-1 flex items-center gap-1 text-[9px] font-mono text-emerald-700"
                      title={`Vendor confirmed this PO via the acceptance link on ${(p.accepted_at || "").slice(0, 16).replace("T", " ")}`}
                      data-testid={`po-vendor-confirmed-${p.id}`}>
                      <CheckCircle size={11} weight="fill" /> Vendor confirmed
                    </div>
                  )}
                  {!p.accepted_at && p.opened_at && (
                    <div className="mt-1 flex items-center gap-1 text-[9px] font-mono text-amber-700"
                      title={`Vendor opened the PO link on ${(p.opened_at || "").slice(0, 16).replace("T", " ")}`}
                      data-testid={`po-vendor-viewed-${p.id}`}>
                      <Eye size={11} weight="fill" /> Vendor viewed
                    </div>
                  )}
                  {receipt.ordered > 0 && (
                    <div
                      className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] font-mono"
                      data-testid={`pos-receipt-summary-${p.id}`}
                    >
                      <span className="text-emerald-700">
                        Received {receipt.recd}/{receipt.ordered}
                      </span>
                      <span className="text-amber-700">
                        Balance {Math.max(0, receipt.ordered - receipt.recd)}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-800">₹ {inr(p.grand_total)}</td>
                <td className="px-4 py-3 text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 text-slate-400 hover:text-emerald-600 border border-slate-200 rounded-sm" data-testid={`pos-actions-${p.id}`}><DotsThreeVertical size={16} weight="bold" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="bg-white border-slate-200 text-slate-700 w-56">
                      <DropdownMenuItem onClick={() => printPO(p, company)} data-testid={`pos-print-${p.id}`}><Eye size={14} className="mr-2" /> View / Print Preview</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => saveToFolder(p)} data-testid={`pos-savefolder-${p.id}`}><FloppyDisk size={14} className="mr-2" /> Save PO (PDF to folder)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openSend(p)} data-testid={`pos-send-${p.id}`}><PaperPlaneTilt size={14} className="mr-2" /> Send PO {p.contact_email ? `to ${p.contact_email}` : "(no vendor email)"}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/vendors?tab=pos&repeat=${p.id}`)} data-testid={`pos-repeat-${p.id}`}><CopySimple size={14} className="mr-2" /> Repeat Order</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-200" />
                      {p.status !== "cancelled" && p.status !== "draft" && (
                        <DropdownMenuItem onClick={() => openGRN(p)} data-testid={`pos-grn-${p.id}`}><Package size={14} className="mr-2" /> Record Material Receipt (GRN)</DropdownMenuItem>
                      )}
                      {["sent", "accepted"].includes(p.status) && p.contact_email && (
                        <DropdownMenuItem onClick={() => remindPO(p, "email")} data-testid={`pos-remind-${p.id}`}><BellRinging size={14} className="mr-2" /> Follow-up Reminder — Email</DropdownMenuItem>
                      )}
                      {["sent", "accepted"].includes(p.status) && p.contact_phone && (
                        <DropdownMenuItem onClick={() => remindPO(p, "whatsapp")} data-testid={`pos-remind-wa-${p.id}`}><ChatCircleText size={14} className="mr-2" /> Follow-up Reminder — WhatsApp</DropdownMenuItem>
                      )}
                      {p.status !== "draft" && p.status !== "cancelled" && (
                        <DropdownMenuItem onClick={() => copyAcceptLink(p)} data-testid={`pos-acceptlink-${p.id}`}><LinkSimple size={14} className="mr-2" /> Copy Vendor Confirmation Link</DropdownMenuItem>
                      )}
                      {(p.revisions || []).length > 0 && (
                        <DropdownMenuItem onClick={() => setHistPO(p)} data-testid={`pos-hist-${p.id}`}><ClockCounterClockwise size={14} className="mr-2" /> Revision History</DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-slate-200" />
                      <DropdownMenuItem onClick={() => downloadFile(p, "pdf")} data-testid={`pos-pdf-${p.id}`}><FilePdf size={14} className="mr-2" /> Download PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadFile(p, "excel")} data-testid={`pos-dl-${p.id}`}><DownloadSimple size={14} className="mr-2" /> Download Excel</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => navigate(`/vendors?tab=pos&edit=${p.id}`)} title="Edit / amend" className="p-1.5 text-slate-400 hover:text-emerald-600" data-testid={`pos-edit-${p.id}`}><PencilSimple size={16} /></button>
                    <button onClick={() => delPO(p)} title="Delete" className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`pos-del-${p.id}`}><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="pos-page-empty">
                {!loaded ? "Loading purchase orders…" : pos.length === 0
                  ? "No purchase orders yet — POs are raised from an Order's Account Section by the Costing team. Vendor details auto-fill from your Vendor Registry."
                  : "No POs match your search / filter."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PO archive — vendor folders */}
      <Dialog open={archOpen} onOpenChange={setArchOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-3xl" data-testid="pos-archive-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">PO Archive — Vendor Folders</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">A copy of every PO is filed automatically in its vendor's folder before it is sent, amended or cancelled — with who did it (initials) and when.</p>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={archQ} onChange={(e) => setArchQ(e.target.value)} placeholder="Search PO no, vendor, initials…"
                className="bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 pl-8 h-8 text-xs focus:border-emerald-600 focus-visible:ring-emerald-600" data-testid="arch-search" />
            </div>
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-zinc-500">From
              <input type="date" value={archFrom} onChange={(e) => setArchFrom(e.target.value)} data-testid="arch-from"
                className="bg-zinc-950 border border-zinc-800 rounded-sm text-zinc-100 text-xs px-2 py-1.5 focus:border-emerald-600 focus:outline-none" />
            </label>
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-zinc-500">To
              <input type="date" value={archTo} onChange={(e) => setArchTo(e.target.value)} data-testid="arch-to"
                className="bg-zinc-950 border border-zinc-800 rounded-sm text-zinc-100 text-xs px-2 py-1.5 focus:border-emerald-600 focus:outline-none" />
            </label>
            {(archQ || archFrom || archTo) && (
              <button onClick={() => { setArchQ(""); setArchFrom(""); setArchTo(""); }} data-testid="arch-clear"
                className="text-[10px] font-mono uppercase tracking-wider text-emerald-600 hover:text-emerald-400 border border-emerald-600/30 bg-emerald-600/10 px-2 py-1.5 rounded-sm">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-[58vh] overflow-y-auto pr-1 space-y-4">
            {(() => {
              const aq = archQ.toLowerCase();
              const filteredArch = archives.filter((a) => {
                const day = (a.archived_at || "").slice(0, 10);
                if (archFrom && day < archFrom) return false;
                if (archTo && day > archTo) return false;
                return !aq
                  || (a.po_no || "").toLowerCase().includes(aq)
                  || (a.folder || "").toLowerCase().includes(aq)
                  || (a.archived_by || "").toLowerCase().includes(aq)
                  || (a.archived_by_initials || "").toLowerCase().includes(aq);
              });
              const groups = Object.entries(filteredArch.reduce((acc, a) => {
                (acc[a.folder] = acc[a.folder] || []).push(a);
                return acc;
              }, {}));
              if (archives.length === 0) return (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-8 text-center" data-testid="arch-empty">
                  No filed copies yet — a copy is stored here automatically the first time a PO is sent, amended or cancelled.
                </div>
              );
              if (groups.length === 0) return (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-8 text-center" data-testid="arch-no-match">
                  No filed copies match your search / date range.
                </div>
              );
              return groups.map(([folder, entries]) => (
              <div key={folder} className="border border-zinc-800 rounded-sm" data-testid={`arch-folder-${folder}`}>
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800">
                  <FolderOpen size={15} className="text-emerald-600" />
                  <span className="text-sm font-semibold text-zinc-100">{folder}</span>
                  <span className="text-[10px] font-mono text-zinc-500 ml-auto">{entries.length} filed cop{entries.length === 1 ? "y" : "ies"}</span>
                </div>
                <div className="divide-y divide-zinc-800">
                  {entries.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`arch-entry-${a.id}`}>
                      <span className={`uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded-sm border shrink-0 ${
                        a.action === "sent" ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                          : a.action === "amended" ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                          : "bg-red-500/10 text-red-400 border-red-500/40"}`}>
                        {a.action === "sent" ? "Before Send" : a.action === "amended" ? "Before Amend" : "Before Cancel"}
                      </span>
                      <span className="font-mono text-emerald-600 truncate">{a.po_no}</span>
                      <span className="font-mono text-zinc-400 shrink-0">{a.archived_by_initials}</span>
                      <span className="font-mono text-zinc-500 shrink-0">{(a.archived_at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className="font-mono text-zinc-300 ml-auto shrink-0">₹ {inr(a.snapshot?.grand_total)}</span>
                      <button onClick={() => printPO({ ...a.snapshot, po_no: a.po_no }, company)} title="View / print this filed copy"
                        className="p-1 text-zinc-400 hover:text-emerald-400 shrink-0" data-testid={`arch-view-${a.id}`}>
                        <Eye size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* GRN — record material receipt */}
      <Dialog open={!!grnPO} onOpenChange={(v) => !v && setGrnPO(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl" data-testid="grn-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Material Receipt — {grnPO?.po_no}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Record quantities received against each line. Partial deliveries are tracked; the PO auto-moves to Material Received once every line is complete.</p>
          </DialogHeader>
          {grnPO && (() => {
            const m = recdMap(grnPO);
            return (
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                      <th className="py-2 pr-2 font-bold">Item</th>
                      <th className="py-2 px-2 font-bold text-right">Ordered</th>
                      <th className="py-2 px-2 font-bold text-right">Received</th>
                      <th className="py-2 px-2 font-bold text-right">Remaining</th>
                      <th className="py-2 pl-2 font-bold text-right">Receive now</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {(grnPO.items || []).filter((it) => Number(it.qty) > 0).map((it) => {
                      const got = Math.min(m[it.id] || 0, Number(it.qty || 0));
                      const rem = Math.max(0, Number(it.qty || 0) - got);
                      return (
                        <tr key={it.id}>
                          <td className="py-2 pr-2 text-zinc-100">{it.item}
                            {it.specification && <div className="text-[10px] text-zinc-500 font-mono">{it.specification}</div>}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-zinc-300">{Number(it.qty)}</td>
                          <td className={`py-2 px-2 text-right font-mono ${got > 0 ? "text-emerald-400" : "text-zinc-500"}`}>{got}</td>
                          <td className={`py-2 px-2 text-right font-mono ${rem > 0 ? "text-amber-400" : "text-zinc-500"}`}>{rem}</td>
                          <td className="py-2 pl-2 text-right">
                            <Input type="number" min="0" max={rem} step="any" disabled={rem <= 0}
                              value={grnQty[it.id] ?? ""} placeholder={rem > 0 ? "0" : "—"}
                              onChange={(e) => setGrnQty((q) => ({ ...q, [it.id]: e.target.value }))}
                              className="w-24 ml-auto h-8 bg-zinc-950 border-zinc-800 rounded-sm text-right font-mono text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600"
                              data-testid={`grn-qty-${it.id}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex gap-3 flex-wrap items-end">
                  <label className="text-[10px] font-mono uppercase text-zinc-500 flex flex-col gap-1">Receipt date
                    <input type="date" value={grnDate} onChange={(e) => setGrnDate(e.target.value)} data-testid="grn-date"
                      className="bg-zinc-950 border border-zinc-800 rounded-sm text-zinc-100 text-xs px-2 py-2 focus:border-emerald-600 focus:outline-none" />
                  </label>
                  <div className="flex-1 min-w-[200px]">
                    <Input value={grnNote} onChange={(e) => setGrnNote(e.target.value)} placeholder="Note (challan no, transporter, remarks…)"
                      className="bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600" data-testid="grn-note" />
                  </div>
                  <Button onClick={saveGRN} data-testid="grn-save"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold">
                    Record Receipt
                  </Button>
                </div>
                {(grnPO.grns || []).length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Receipt history</div>
                    <div className="space-y-2">
                      {[...(grnPO.grns || [])].reverse().map((g) => (
                        <div key={g.grn_no} className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-2.5 text-xs" data-testid={`grn-hist-${g.grn_no}`}>
                          <div className="flex justify-between flex-wrap gap-1 mb-1">
                            <span className="font-bold text-emerald-600 font-mono">{g.grn_no} · {g.date}</span>
                            <span className="text-zinc-500 font-mono">by {g.received_by_initials} · {(g.received_at || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                          {(g.entries || []).map((e, i) => (
                            <div key={i} className="text-zinc-300 font-mono text-[11px]">• {e.item} — {e.qty_received} recd</div>
                          ))}
                          {g.note && <div className="text-zinc-500 mt-1 italic">{g.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Send / place PO — channel choice */}
      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-sm" data-testid="pos-send-dialog">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Send {sendFor?.po_no} to Vendor</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Choose how to notify {sendFor?.vendor_name}.</p>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-200">
              <Checkbox checked={sendChannels.email} disabled={!sendFor?.contact_email} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, email: !!v }))} data-testid="pos-send-email" />
              Email {sendFor?.contact_email ? `(${sendFor.contact_email})` : "(no vendor email on file)"}
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-200">
              <Checkbox checked={sendChannels.whatsapp} disabled={!sendFor?.contact_phone} onCheckedChange={(v) => setSendChannels((c) => ({ ...c, whatsapp: !!v }))} data-testid="pos-send-whatsapp" />
              WhatsApp {sendFor?.contact_phone ? `(${sendFor.contact_phone})` : "(no vendor phone on file)"}
            </label>
          </div>
          <Button onClick={confirmSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold mt-2" data-testid="pos-send-confirm">
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* revision history dialog */}
      <Dialog open={!!histPO} onOpenChange={(v) => !v && setHistPO(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader className="border-b border-zinc-800 pb-3">
            <DialogTitle className="font-heading text-lg font-extrabold uppercase tracking-wide text-emerald-600">Revision History — {histPO?.po_no}</DialogTitle>
            <p className="text-xs text-zinc-500 font-normal normal-case">Current grand total ₹ {inr(histPO?.grand_total)}. Each entry shows the PO as it was <b>before</b> that amendment.</p>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {[...(histPO?.revisions || [])].reverse().map((r) => (
              <div key={r.rev_no} className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-3" data-testid={`pos-rev-${r.rev_no}`}>
                <div className="flex justify-between text-xs mb-2 flex-wrap gap-1">
                  <span className="font-bold text-emerald-600 font-mono">Rev {r.rev_no}</span>
                  <span className="text-zinc-500 font-mono">by {r.edited_by_initials ? `${r.edited_by_initials} (${r.edited_by})` : r.edited_by} · {(r.edited_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-xs text-zinc-400 mb-1.5">
                  Vendor <span className="text-zinc-200">{r.prev?.vendor_name}</span> · GST {r.prev?.gst_percent}% ·
                  grand total <span className="font-mono text-zinc-100">₹ {inr(r.prev?.grand_total)}</span> · status {r.prev?.status}
                </div>
                <div className="space-y-0.5">
                  {(r.prev?.items || []).map((it, i) => (
                    <div key={i} className="text-[11px] text-zinc-300 font-mono">• {it.item} — {it.qty} × ₹{it.rate}{Number(it.dis) ? ` (−${it.dis}%)` : ""}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
